import React, { useState } from 'react';
import { Mail, Send, CheckCircle2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import axiosInstance from '../../api/axios';

const TEMPLATES = [
  { id: 'welcome',    label: 'Willkommens-Mail',        emoji: '🌸', desc: 'Mit Voucher-Code „WELCOME1_…"' },
  { id: 'newsletter', label: 'Newsletter-Begrüßung',     emoji: '💌', desc: '15 % Rabatt-Code für Newsletter-Abonnent:innen' },
  { id: 'bank',       label: 'Bankdaten-Mail',           emoji: '🏦', desc: 'Banküberweisungs-Anweisungen (IBAN/BIC)' },
  { id: 'order',      label: 'Bestellbestätigung',        emoji: '📦', desc: 'Beispielbestellung mit 2 Artikeln' },
];

const TestEmail = () => {
  const [recipient, setRecipient] = useState('');
  const [name, setName] = useState('Anna Tester');
  const [loading, setLoading] = useState(null);
  const [lastSent, setLastSent] = useState(null);

  const send = async (template) => {
    if (!recipient.trim()) {
      toast.error('Bitte E-Mail-Adresse eintragen');
      return;
    }
    setLoading(template);
    try {
      await axiosInstance.post('/admin/test-email', {
        template,
        recipient: recipient.trim(),
        name: name.trim() || 'Anna Tester',
      });
      setLastSent({ template, at: new Date().toLocaleTimeString('de-DE') });
      toast.success(`✓ Test-Mail „${TEMPLATES.find(t => t.id === template)?.label}" gesendet`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Versand fehlgeschlagen');
    } finally {
      setLoading(null);
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="test-email-page">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 bg-[#C4A084] flex items-center justify-center">
          <Mail className="h-5 w-5 text-white" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-[#5D4E3C]" style={{ fontFamily: 'Georgia, serif' }}>
            Test-E-Mail senden
          </h1>
          <p className="text-xs text-[#9C8C7A]">Schick dir die Templates aufs Handy zur optischen Prüfung.</p>
        </div>
      </div>

      <div className="bg-white border border-[#E8DCC8] p-4 sm:p-5 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-[#5D4E3C] mb-1 block">Empfänger-Adresse</Label>
            <Input
              type="email"
              value={recipient}
              onChange={(e) => setRecipient(e.target.value)}
              placeholder="z.B. yasemink1213@gmail.com"
              data-testid="test-email-recipient"
              className="rounded-none"
            />
          </div>
          <div>
            <Label className="text-xs text-[#5D4E3C] mb-1 block">Name (optional)</Label>
            <Input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Anna Tester"
              data-testid="test-email-name"
              className="rounded-none"
            />
          </div>
        </div>
      </div>

      <div className="space-y-2">
        {TEMPLATES.map((tpl) => (
          <div
            key={tpl.id}
            className="bg-white border border-[#E8DCC8] p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3"
          >
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <span className="text-2xl">{tpl.emoji}</span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-[#5D4E3C]">{tpl.label}</p>
                <p className="text-xs text-[#9C8C7A] truncate">{tpl.desc}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {lastSent?.template === tpl.id && (
                <span className="text-[10px] text-[#5D8A5A] flex items-center gap-1">
                  <CheckCircle2 className="h-3 w-3" /> {lastSent.at}
                </span>
              )}
              <Button
                onClick={() => send(tpl.id)}
                disabled={loading === tpl.id || !recipient.trim()}
                size="sm"
                className="rounded-none bg-[#5D4E3C] hover:bg-[#3E2723] text-white whitespace-nowrap"
                data-testid={`send-test-${tpl.id}`}
              >
                <Send className="h-3.5 w-3.5 mr-1.5" />
                {loading === tpl.id ? 'Sende…' : 'Test senden'}
              </Button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-[11px] text-[#9C8C7A] italic mt-4">
        Tipp: Bei Apple Mail muss der „Bilder anzeigen"-Hinweis einmal getippt werden,
        damit das Logo geladen wird. Voucher-Codes &amp; IBAN sind bewusst Beispiele.
      </p>
    </div>
  );
};

export default TestEmail;
